import React, { useContext, useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Eye } from 'react-feather';
import { useSelector } from 'react-redux';
import { RootState } from 'app/reducer';
import dayjs from 'dayjs';

import UserPassport from 'components/profile/UserPassport';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import JoinGroupPassportButton from 'components/group/JoinGroupPassportButton';
import { ANON_TYPE } from 'constants/app';
import ProductLinkList from 'components/product/ProductLinkList';
import Link from 'components/misc/Link';
import {
  Image,
  MarkdownTruncate,
  Zoomable,
  Tag,
} from 'seedly-component-library';
import Pin from 'seedly-component-library/icons/pin.svg';
import { TrendingContentContext } from 'pageContents/CommunityContent/context/TrendingContentContext';
import PostCommentProvider from 'components/comment/CommentContext/PostCommentContext';
import { CommunityPageContext } from 'pageContents/CommunityContent/context/CommunityPageContext';

import { convertDateFormat } from 'utils/convertDateFormat';
import { generateCommentLink } from 'utils/generateShareLink';
import CommentList from 'components/comment/CommentList';
import { Post } from 'app/entity/post/postTypes';
import PostCommentForm from 'components/comment/PostCommentForm';
import useTrackSmartFeed from 'utils/hooks/useTrackSmartFeed';
import useLinkPreview from 'utils/hooks/useLinkPreview';
import clsx from 'clsx';
import EngagementBar from './EngagementBar';
import PostEncouragement from '../PostEncouragement';

interface OwnProps {
  post: Post;
  withComment?: boolean;
  isOwnProfile?: boolean;
  enableTitleAsLink?: boolean;
  enableToggle?: boolean;
}

const PostItem = (props: OwnProps) => {
  const {
    post,
    withComment = false,
    isOwnProfile = false,
    enableTitleAsLink = true,
    enableToggle = false,
  } = props;

  const {
    id,
    title,
    slug,
    description,
    isAnonymous,
    createdAt,
    updatedAt,
    parentCommentsCount,
    user,
    post: postDetail,
    createdByCurrentUser,
    picture,
    pictureOriginal,
    group,
    campaignPostState,
  } = post;

  const isPinned = campaignPostState === 'live';
  const productsList = get(postDetail, 'productsList', []);
  const seedlyEvent = get(postDetail, 'seedlyeventList[0]', undefined);

  const dateText =
    createdAt && updatedAt && convertDateFormat(createdAt, updatedAt);

  const shouldRenderOnlyVisibleTag =
    createdByCurrentUser && isAnonymous && isOwnProfile;

  const { followedGroupIds = [] } = useContext(TrendingContentContext);
  const { pageType } = useContext(CommunityPageContext);

  const isFollowed = followedGroupIds.indexOf(group?.id) !== -1;

  const { linkPreviewData, linkPreviewURL } = useLinkPreview(description);
  const [shouldTruncate, toogleTruncate] = useState(false);
  const [displayImage, setDisplayImage] = useState(null);
  const profile = useSelector((state: RootState) => state.profile.user);

  useEffect(() => {
    if (description && description.length > 400 && enableToggle) {
      toogleTruncate(true);
    }
  }, [description]);

  useEffect(() => {
    if (picture || linkPreviewData.image_url) {
      setDisplayImage(picture || linkPreviewData.image_url);
    }

    return () => setDisplayImage(null);
  }, [picture, linkPreviewData.image_url]);

  // TODO: refactor post item to be more modular Github Issue #1613
  const displayGroupName = group && (
    <>
      <p className="text-xs text-neutral-500">∙</p>
      <Link href={`/community/${group.slug}`}>
        <a className="text-xs text-neutral-500 hover:text-blue-500">
          {group.name}
        </a>
      </Link>
    </>
  );

  const ref = useRef<HTMLDivElement>();
  useTrackSmartFeed<HTMLDivElement>(ref, 2000, {
    feed: pageType,
    param: 'post',
    param_id: id,
  });

  return (
    <div
      className={clsx('card', isPinned && 'p-0')}
      data-testid="post-item-card"
    >
      {isPinned && (
        <div className="flex items-center rounded-t-none lg:rounded-t-lg bg-blue-500 py-3 px-4">
          <Pin className="mr-2" />
          <p className="text-white">Pinned by Seedly</p>
        </div>
      )}
      <div className={clsx('flex flex-col gap-4 w-full', isPinned && 'p-4')}>
        {shouldRenderOnlyVisibleTag && (
          <div className="flex gap-2 items-center">
            <Eye size={14} className="text-neutral-500" />
            <p className="text-xs text-neutral-500">Only visible to you</p>
            <p className="text-xs text-neutral-500">{dateText}</p>
            {displayGroupName}
          </div>
        )}
        {/* if is anonymous and is not poster */}
        {isAnonymous && !isOwnProfile && (
          <div className="flex items-baseline gap-2">
            <p className="text-xs text-neutral-600">Anonymous</p>
            <div className="flex items-baseline gap-1">
              <p className="text-xs text-neutral-500">{dateText}</p>
              {displayGroupName}
              {!isFollowed && pageType === 'trending' && (
                <>
                  <p className="text-xs text-neutral-500">∙</p>
                  <JoinGroupPassportButton groupId={group.id} />
                </>
              )}
            </div>
          </div>
        )}
        {!isAnonymous && !isEmpty(user) && (
          <UserPassport
            profile={createdByCurrentUser ? profile : user}
            createdAt={createdAt}
            updatedAt={updatedAt}
            anonymousType={isAnonymous && ANON_TYPE.QUESTION}
            credential={user && user.defaultCredential}
            avatarSize="avatar-12"
            postedGroup={group}
            joinGroupButton={
              !isFollowed &&
              pageType === 'trending' && (
                <>
                  <p className="text-xs text-neutral-500">∙</p>
                  <JoinGroupPassportButton groupId={group.id} />
                </>
              )
            }
          />
        )}
        <div className="flex flex-col w-full gap-2">
          {isPinned && (
            <div className="p-1 rounded bg-red-500 w-fit">
              <p className="text-white text-[10px] font-bold">LIVE</p>
            </div>
          )}
          {campaignPostState === 'closed' && (
            <div className="p-1 rounded bg-neutral-700 w-fit">
              <p className="text-white text-[10px] font-bold">CLOSED</p>
            </div>
          )}
          <div ref={ref} className="flex flex-col w-full gap-2">
            {enableTitleAsLink ? (
              <Link href={`/posts/${slug}`} passHref>
                <a
                  className="break-words text-base font-bold underline"
                  rel="noopener noreferrer"
                  data-testid="post-item-title"
                >
                  {title}
                </a>
              </Link>
            ) : (
              <h1
                className="break-words text-base font-bold"
                data-testid="post-item-title"
              >
                {title}
              </h1>
            )}
            {description && (
              <MarkdownTruncate
                text={description}
                shouldTruncate={shouldTruncate}
                toogleTruncate={toogleTruncate}
                onReadMoreClick={() => {
                  mixpanel.track(trackedEvents.ReadMoreClicked, {
                    param: 'post',
                    param_id: id,
                  });
                }}
                truncateLine={3}
                extendedClass="post-desc-body"
              />
            )}
          </div>
          {displayImage &&
            (picture ? (
              <Zoomable
                onZoom={() => {
                  setDisplayImage(pictureOriginal);
                }}
                onUnzoom={() => {
                  setDisplayImage(picture);
                }}
              >
                <Image alt="Post Image" picture={displayImage} />
              </Zoomable>
            ) : (
              <a
                target="_blank"
                rel="nofollow noopener noreferrer"
                href={linkPreviewURL}
              >
                <Image
                  alt="Post Image"
                  picture={displayImage}
                  linkPreviewData={linkPreviewData}
                />
              </a>
            ))}
        </div>
        {productsList.length > 0 && (
          <ProductLinkList productTags={productsList} />
        )}
        {seedlyEvent && (
          <Link href={`/community/${group?.slug}?filter=${seedlyEvent.slug}`}>
            <Tag variant="green" renderAs="a" title={seedlyEvent.name} />
          </Link>
        )}
        {post.user?.firstPostId === id &&
          !post.createdByCurrentUser &&
          dayjs(new Date()).diff(dayjs(post.createdAt), 'day') < 8 && (
            <PostEncouragement name={post.user?.name} type="post" />
        )}
        <EngagementBar post={post} />
        {withComment && (
          <>
            <div className="horizontal-divider " />
            <PostCommentProvider post={post}>
              <PostCommentForm commentableType="Post" commentableId={id} />
              <CommentList
                totalCommentCount={parentCommentsCount}
                commentableId={post.id}
                commentableType="Post"
                childCommentableType="PostComment"
                commentShareLink={generateCommentLink(post.slug)}
                enableTruncateOnFirstComment
              />
            </PostCommentProvider>
          </>
        )}
      </div>
    </div>
  );
};

export default PostItem;
